/**
 * Shared builder: PL `HalfBannerHalfProductTilesWidgetData` → domain `CustomSection`.
 *
 * Mirrors `banner-section-builders.ts` and `featured-products-showcase-section-builders.ts`: dependency-light,
 * no JSX, so the widget can render the existing `HalfBannerHalfProductTiles` section component
 * (DS + legacy routing + product-card analytics) without duplicating the proto→domain conversion.
 *
 * Product cards are NOT embedded in the widget data — they arrive as nested `Widget[]`
 * (SINGLE_PRODUCT_CARD → productItem), enriched at PL serve time exactly like
 * PRODUCT_LIST / PRODUCT_GRID_CAROUSEL, and render as pre-rendered `renderedChildren`
 * (the shared SINGLE_PRODUCT_CARD leaf) arranged by the container widget — so this
 * builder only converts the section-level fields (banner, heading, analytics).
 */

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import type { ContentfulImageForMenuItem } from '@src/quince-site-common/src/domains/contentful/ContentfulImage'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import Image from '@src/quince-site-common/src/domains/Image'

import { makeSection, protoContainerLinksToLinks } from './banner-section-builders'
import { buildWidgetTrackingMetaData, protoButtonToDomain, toRichText } from './widget-utils'

import type { HalfBannerHalfProductTilesWidgetData, Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * Extract flat image objects from proto media for HeroBannerV2.
 * HeroBannerV2 (via useContentWithBackgroundImageProps) expects images: [{url, title, height, width}].
 */
function protoMediaToImages(media: Media | undefined): Record<string, unknown>[] {
  if (!media || media.type !== MediaType.IMAGE || !media.content) return []
  const c = media.content as Record<string, unknown>
  if (c.url) {
    return [c]
  }
  if (c.image && typeof c.image === 'object') {
    return [c.image as Record<string, unknown>]
  }
  return []
}

export function buildHalfBannerHalfProductTilesConfig(data: HalfBannerHalfProductTilesWidgetData): CustomSection {
  const heading = toRichText(data.heading as Parameters<typeof toRichText>[0])
  const description = toRichText(data.description as Parameters<typeof toRichText>[0])

  const richTexts = [heading, description].filter(Boolean) as CustomSection['richTexts']

  const images = protoMediaToImages(data.media).map(raw =>
    Image.fromContentful(raw as unknown as ContentfulImageForMenuItem),
  ) as unknown as CustomSection['images']

  return makeSection({
    id: data.id,
    sectionType: CUSTOM_SECTION_TYPE.HALF_BANNER_HALF_PRODUCT_TILES,
    images,
    richTexts,
    buttons: data.buttons.map(protoButtonToDomain) as CustomSection['buttons'],
    // Cards are not part of the section config — they arrive as nested SINGLE_PRODUCT_CARD
    // widgets and render as pre-rendered children (see file header).
    productCards: [],
    numberOfProductsToShowInDesktop: data.numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile: data.numberOfProductsToShowInMobile,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
    containerLinks: protoContainerLinksToLinks(data.containerLinks) as CustomSection['containerLinks'],
    // Contentful section classes (commonClassNames / mobileClassNames / baseDynamicValueClasses /
    // dynamicValueClassesMobile) — both forks apply them via useDynamicStyle, same as non-PL.
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    mobileDynamicClassesOverrides: data.mobileDynamicClassesOverrides,
  })
}
